import {
  type ActiveElement,
  BarController,
  BarElement,
  CategoryScale,
  type ChartEvent,
  Chart as ChartJS,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  type ScriptableContext,
  type ScriptableLineSegmentContext,
  Title,
  Tooltip,
} from "chart.js";
import { Chart } from "react-chartjs-2";

import { useFormatDate } from "@/hooks/date";
import { divideByScale } from "@/utils/number/scale";

import {
  BAR_WIDTH,
  CHART_AREA_MARGIN_LEFT,
  CHART_PRIMARY_COLOR,
} from "./constants";
import { useCompoundChartContext } from "./context";

ChartJS.register(
  BarController,
  LineController,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export function CompoundChartCore() {
  const { data, scales, onHoverDatumChange } = useCompoundChartContext();
  const formatDate = useFormatDate();

  const getSegmentDashStyle = (ctx: ScriptableLineSegmentContext) => {
    const nextDatum = data[ctx.p1DataIndex];
    const isFuture = nextDatum?.isForecasted;
    if (isFuture) {
      return [5, 5];
    }
    return [];
  };

  const chartData = {
    labels: data.map((item) => formatDate(item.date, "MMM")),
    datasets: [
      {
        type: "line" as const,
        label: "Monthly Value",
        data: data.map((item) => item.value),
        borderColor: CHART_PRIMARY_COLOR,
        backgroundColor: CHART_PRIMARY_COLOR,
        tension: 0,
        yAxisID: "y",
        segment: {
          borderDash: getSegmentDashStyle,
        },
        pointRadius: 0,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: CHART_PRIMARY_COLOR,
        pointHoverBorderColor: "white",
        pointHoverBorderWidth: 2,
      },
      {
        type: "bar" as const,
        label: "Cumulative Value",
        data: data.map((item) => item.cumulative),
        backgroundColor: (ctx: ScriptableContext<"bar">) => {
          const datum = data[ctx.dataIndex];
          if (datum?.isForecasted) {
            return createStripedPattern(ctx.chart.ctx);
          }
          return CHART_PRIMARY_COLOR;
        },
        borderColor: CHART_PRIMARY_COLOR,
        borderWidth: 1,
        yAxisID: "y1",
        barThickness: BAR_WIDTH,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: CHART_AREA_MARGIN_LEFT,
      },
    },
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          callback: function (_value: string | number, index: number) {
            const date = data[index]?.date;
            if (!date) return "";

            const monthText = formatDate(date, "MMM");
            const isFirstOrJanuary = index === 0 || date.getMonth() === 0;

            if (isFirstOrJanuary) {
              return [monthText, formatDate(date, "yyyy")];
            }
            return monthText;
          },
          maxRotation: 0,
          font: {
            size: 13,
          },
          color: "#444",
        },
      },
      y: {
        type: "linear" as const,
        display: true,
        position: "left" as const,
        beginAtZero: true,
        grid: {
          color: "#E5E5E5",
        },
        ticks: {
          callback: function (value: string | number) {
            const numValue =
              typeof value === "string" ? parseFloat(value) : value;
            return divideByScale(numValue, scales.valueScale).toString();
          },
          font: {
            size: 13,
          },
          color: "#525252",
        },
      },
      y1: {
        type: "linear" as const,
        display: true,
        position: "right" as const,
        beginAtZero: true,
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          callback: function (value: string | number) {
            const numValue =
              typeof value === "string" ? parseFloat(value) : value;
            return divideByScale(numValue, scales.cumulativeScale).toString();
          },
          font: {
            size: 13,
          },
          color: "#525252",
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: function (context: {
          tooltip: {
            opacity: number;
            dataPoints?: Array<{ dataIndex: number }>;
          };
        }) {
          const tooltipModel = context.tooltip;

          if (tooltipModel.opacity === 0) {
            onHoverDatumChange(undefined);
            return;
          }

          const dataIndex = tooltipModel.dataPoints?.[0]?.dataIndex;
          if (dataIndex !== undefined && data[dataIndex]) {
            onHoverDatumChange(data[dataIndex]);
          }
        },
      },
    },
    onHover: (_event: ChartEvent, activeElements: ActiveElement[]) => {
      if (activeElements.length > 0) {
        const dataIndex = activeElements[0].index;
        if (data[dataIndex]) {
          onHoverDatumChange(data[dataIndex]);
        }
      } else {
        onHoverDatumChange(undefined);
      }
    },
  };

  return <Chart type="line" data={chartData} options={options} />;
}

function createStripedPattern(
  ctx: CanvasRenderingContext2D,
): CanvasPattern | string {
  const canvas = document.createElement("canvas");
  const patternCtx = canvas.getContext("2d");

  if (!patternCtx) return CHART_PRIMARY_COLOR;

  canvas.width = 8;
  canvas.height = 8;

  patternCtx.strokeStyle = CHART_PRIMARY_COLOR;
  patternCtx.lineWidth = 1;
  patternCtx.beginPath();
  patternCtx.moveTo(0, 0);
  patternCtx.lineTo(0, 8);
  patternCtx.stroke();

  const pattern = ctx.createPattern(canvas, "repeat");
  return pattern || CHART_PRIMARY_COLOR;
}
